/* /guide — the two-minute version, for someone who has never seen this before.
 *
 * One page, under 800 words, no jargon that is not defined on it, and one
 * section that exists because the example document could otherwise mislead:
 * DOE/FEMP's teaching template is a composite, and some of what Ceiling finds
 * in it may be an artefact of how it was assembled. That is said here plainly
 * rather than buried in a note, because a reader who does not know it may draw
 * the wrong conclusion about both the plan and the tool.
 *
 * The copy lives in this file rather than in a README the page renders: there
 * is no README with this content in the repository, and a second copy of the
 * prose would be the version drift FM-04 exists to catch.
 */

import { Link } from 'react-router-dom';

import { Button, EvidenceBadge, Label } from '../components/primitives';
import { EVIDENCE_BADGES } from '../data/report';
import { PISCATAWAY } from '../data/seed';
import { MAINTAINER_EMAIL } from '../report/decisions';
import './Guide.css';

const BADGE_IDS = ['arithmetic', 'audit-precedent', 'guidance-warning'] as const;

export default function Guide() {
  return (
    <div className="gd">
      <header className="gd__header">
        <div>
          <Label mono>Guide · Two minutes</Label>
          <h1 className="t-display-2">Start here.</h1>
        </div>
        <div>
          <p className="gd__lede">
            What Ceiling is, what it reads, what the example document is, and
            where it stops.
          </p>
        </div>
      </header>

      <section className="gd__section">
        <h2 className="gd__head">What this is</h2>
        <p className="gd__prose">
          Ceiling reads a federal ESPC measurement and verification plan before
          anyone signs it, and points at the clauses that have caused real
          disputes. For each one it quotes the clause, and then either shows the
          arithmetic or names the federal audit where the same cause cost an
          agency money. It runs entirely in your browser: the document is never
          uploaded, and there is no account and no server holding your work.
        </p>
      </section>

      <section className="gd__section">
        <h2 className="gd__head">What document it reads</h2>
        <p className="gd__prose">
          An M&amp;V plan or an investment grade audit, as a PDF that has real
          text in it. A scan or a photocopy has no text layer, and Ceiling
          refuses it rather than reading it to zero findings. It does not read
          the contract, the task order, or anything the plan refers to but does
          not contain.
        </p>
      </section>

      <section className="gd__section">
        <h2 className="gd__head">
          What the example document is, and why that matters
        </h2>
        <p className="gd__prose">
          The worked example is DOE/FEMP&rsquo;s{' '}
          <a
            href={PISCATAWAY.documentUrl}
            target="_blank"
            rel="noreferrer noopener"
          >
            Example Measurement &amp; Verification Plan
          </a>{' '}
          of February 2007 — {PISCATAWAY.facility}, Delivery Order{' '}
          {PISCATAWAY.deliveryOrder}. It is a teaching template, published to
          show what a compliant plan looks like. It is not a live contract, the
          delivery order number is not a real one, and it was assembled from
          more than one project. That last part matters: some of what looks
          inconsistent in it may be an artefact of the assembly rather than a
          mistake anyone made on a real job. It is still the right document to
          learn on, because it is the government&rsquo;s own model of good
          practice and it is public — but do not read a finding in it as an
          accusation about a project.
        </p>
      </section>

      <section className="gd__section">
        <h2 className="gd__head">How to read a finding</h2>
        <p className="gd__prose">
          A finding opens with one sentence in plain language, and where it is
          in the plan. Open &ldquo;Show the check&rdquo; for the clause quoted
          word for word and, where the check is arithmetic, the figures and the
          operation, so you can redo it on paper. Open &ldquo;Where this comes
          from&rdquo; for the audit case, the FEMP matrix row and the internal
          code. Nothing between those layers is a model&rsquo;s opinion of your
          plan: the only text Ceiling shows from your document is text found in
          your document.
        </p>
      </section>

      <section className="gd__section">
        <h2 className="gd__head">What the three evidence badges mean</h2>
        <dl className="gd__badges">
          {BADGE_IDS.map((id) => (
            <div key={id} className="gd__badge">
              <dt>
                <EvidenceBadge id={id} />
              </dt>
              <dd>{EVIDENCE_BADGES[id].definition}</dd>
            </div>
          ))}
        </dl>
      </section>

      <section className="gd__section">
        <h2 className="gd__head">What to do with the findings</h2>
        <p className="gd__prose">
          Decide on each one: include it as a comment, mark it not a concern, or
          hand it to someone who knows better than you do. Every decision you
          make builds a comment sheet on FEMP&rsquo;s own P3-09 review form,
          which you can edit and export as Word or Excel and send to the
          contractor. Your decisions stay in this browser and come back when you
          return.
        </p>
      </section>

      <section className="gd__section">
        <h2 className="gd__head">What Ceiling will not do</h2>
        <p className="gd__prose">
          It does not score a plan, rank findings by severity, tell you whether
          to sign, or write contract language for you — the moment it drafted
          language it would stop being traceable, which is the only thing it has
          worth having. Fifteen checks run, so a plan with no findings is a plan
          these fifteen checks found nothing in, and nothing more than that.{' '}
          <Link to="/method">How it works</Link> states every boundary in full.
        </p>
      </section>

      <section className="gd__section">
        <h2 className="gd__head">Who made it and why</h2>
        <p className="gd__prose">
          An independent project, not affiliated with DOE, FEMP or any agency.
          It was built after reading federal audit after federal audit describe
          the same handful of causes — an unwitnessed baseline, a stipulated
          number nobody revisits, two tables that never agreed — and noticing
          that all of them were visible in the plan before signature, to anyone
          who read it that way. Corrections are welcome:{' '}
          <a href={`mailto:${MAINTAINER_EMAIL}`}>{MAINTAINER_EMAIL}</a>.
        </p>
      </section>

      <div className="gd__actions">
        <Button variant="primary" marker to={`/review/${PISCATAWAY.documentId}`}>
          Open the example review
        </Button>
        <Button variant="secondary" marker to="/checks">
          See the fifteen checks
        </Button>
      </div>
    </div>
  );
}
